import React, { useState, useEffect } from 'react';
import { getSmtpConfigs, createSmtpConfig, testSmtpConfig, sendTestEmail, deleteSmtpConfig } from '../api';
import toast from 'react-hot-toast';
import { Settings, Plus, Trash2, X, CheckCircle, Wifi, Server, SendHorizontal } from 'lucide-react';

const PRESETS = {
  gmail: { smtpHost:'smtp.gmail.com',smtpPort:587,smtpSecure:false,imapHost:'imap.gmail.com',imapPort:993,imapSecure:true },
  hostinger: { smtpHost:'smtp.hostinger.com',smtpPort:465,smtpSecure:true,imapHost:'imap.hostinger.com',imapPort:993,imapSecure:true },
  outlook: { smtpHost:'smtp-mail.outlook.com',smtpPort:587,smtpSecure:false,imapHost:'outlook.office365.com',imapPort:993,imapSecure:true },
  sendgrid: { smtpHost:'smtp.sendgrid.net',smtpPort:587,smtpSecure:false,imapHost:'',imapPort:993,imapSecure:true },
  custom: { smtpHost:'',smtpPort:587,smtpSecure:false,imapHost:'',imapPort:993,imapSecure:true },
};

const init = { name:'',provider:'gmail',smtpHost:'smtp.gmail.com',smtpPort:587,smtpSecure:false,imapHost:'imap.gmail.com',imapPort:993,imapSecure:true,user:'',pass:'',fromName:'',fromEmail:'',isDefault:false };

export default function SmtpSettings() {
  const [configs, setConfigs] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(init);
  const [testing, setTesting] = useState(false);
  const [testEmailAddr, setTestEmailAddr] = useState('');
  const [showTest, setShowTest] = useState(null);

  useEffect(() => { load(); }, []);
  const load = async () => { try { setConfigs((await getSmtpConfigs()).data); } catch(e){ toast.error('Failed to load'); } };

  const setProvider = (p) => { setForm({...form, provider:p, ...PRESETS[p]}); };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await testSmtpConfig(form);
      r.data.success ? toast.success('Connection OK!') : toast.error(r.data.message);
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
    setTesting(false);
  };

  const handleSave = async () => {
    if (!form.name||!form.user||!form.pass||!form.fromEmail) return toast.error('Fill all required fields');
    try {
      await createSmtpConfig(form);
      toast.success('Saved!');
      setShow(false); setForm(init); load();
    } catch(e) { toast.error('Failed'); }
  };

  const handleSendTest = async (configId) => {
    if (!testEmailAddr) return toast.error('Enter a test email address');
    try {
      const r = await sendTestEmail({ configId, testEmail: testEmailAddr });
      r.data.success ? toast.success(`Test email sent! Check ${testEmailAddr}`) : toast.error(r.data.message);
      setShowTest(null); setTestEmailAddr('');
    } catch(e) { toast.error(e.response?.data?.message || 'Failed to send test'); }
  };

  return (<>
    <div className="pg-hdr">
      <h2>SMTP Settings</h2>
      <p>Configure your email sending accounts (Gmail, Hostinger, etc.)</p>
      <div className="pg-acts"><button className="btn btn-p" onClick={()=>setShow(true)}><Plus size={15}/> Add SMTP Config</button></div>
    </div>
    <div className="pg-body">
      {configs.length===0 ? <div className="card"><div className="empty"><Server size={44}/><h3>No SMTP configured</h3><p>Add your email server to start sending</p></div></div> : (
        <div style={{display:'grid',gap:10}}>{configs.map(c=>(
          <div className="card" key={c._id} style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div style={{display:'flex',alignItems:'center',gap:12}}>
              <div className="stat-icon si-a"><Server size={17}/></div>
              <div>
                <div style={{fontWeight:600,fontSize:13.5}}>{c.name} {c.isDefault&&<span className="badge b-delivered" style={{marginLeft:6}}>Default</span>} <span className="badge" style={{background:'var(--bg3)',color:'var(--txtm)',marginLeft:4}}>{c.provider||'custom'}</span></div>
                <div style={{fontSize:11.5,color:'var(--txtm)',fontFamily:'var(--mono)'}}>{c.smtpHost}:{c.smtpPort} • {c.fromName} &lt;{c.fromEmail}&gt;</div>
                {c.imapHost && <div style={{fontSize:10.5,color:'var(--txtm)'}}>IMAP: {c.imapHost}:{c.imapPort} (reply tracking enabled)</div>}
              </div>
            </div>
            <div style={{display:'flex',gap:6}}>
              <button className="btn btn-s btn-sm" onClick={()=>setShowTest(c._id)}><SendHorizontal size={13}/> Test Send</button>
              <button className="btn btn-d btn-sm" onClick={async()=>{if(!window.confirm('Delete?'))return;await deleteSmtpConfig(c._id);toast.success('Deleted');load();}}><Trash2 size={13}/></button>
            </div>
          </div>
        ))}</div>
      )}

      <div className="card" style={{marginTop:20}}>
        <div className="card-title" style={{marginBottom:10}}>Setup Guide</div>
        <p style={{fontSize:12.5,color:'var(--txt2)',lineHeight:1.8}}>
          <strong>Gmail:</strong> Enable 2FA → Generate App Password at myaccount.google.com/apppasswords<br/>
          <strong>Hostinger:</strong> Use your Hostinger email credentials directly<br/>
          <strong>Outlook:</strong> Use smtp-mail.outlook.com with your Microsoft account<br/>
          <strong>SendGrid:</strong> Username: "apikey", Password: your API key
        </p>
      </div>
    </div>

    {show && <div className="modal-bg" onClick={()=>setShow(false)}><div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
      <div className="modal-hdr"><h3>Add SMTP Configuration</h3><button className="btn btn-g btn-sm" onClick={()=>setShow(false)}><X size={15}/></button></div>
      <div className="modal-body">
        <div className="fg"><label className="lbl">Provider</label>
          <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
            {['gmail','hostinger','outlook','sendgrid','custom'].map(p=>(
              <button key={p} className={`btn btn-sm ${form.provider===p?'btn-p':'btn-s'}`} onClick={()=>setProvider(p)}>{p.charAt(0).toUpperCase()+p.slice(1)}</button>
            ))}
          </div>
        </div>
        <div className="fg"><label className="lbl">Config Name *</label><input className="inp" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="My Gmail Account"/></div>
        <div className="fr3">
          <div className="fg"><label className="lbl">SMTP Host</label><input className="inp" value={form.smtpHost} onChange={e=>setForm({...form,smtpHost:e.target.value})}/></div>
          <div className="fg"><label className="lbl">SMTP Port</label><input className="inp" type="number" value={form.smtpPort} onChange={e=>setForm({...form,smtpPort:+e.target.value})}/></div>
          <div className="fg"><label className="lbl">Secure (SSL)</label><select className="inp sel" value={form.smtpSecure?'true':'false'} onChange={e=>setForm({...form,smtpSecure:e.target.value==='true'})}><option value="false">No (STARTTLS)</option><option value="true">Yes (SSL)</option></select></div>
        </div>
        <div className="fr3">
          <div className="fg"><label className="lbl">IMAP Host (for replies)</label><input className="inp" value={form.imapHost} onChange={e=>setForm({...form,imapHost:e.target.value})}/></div>
          <div className="fg"><label className="lbl">IMAP Port</label><input className="inp" type="number" value={form.imapPort} onChange={e=>setForm({...form,imapPort:+e.target.value})}/></div>
          <div className="fg"><label className="lbl">IMAP Secure</label><select className="inp sel" value={form.imapSecure?'true':'false'} onChange={e=>setForm({...form,imapSecure:e.target.value==='true'})}><option value="true">Yes</option><option value="false">No</option></select></div>
        </div>
        <div className="fr2">
          <div className="fg"><label className="lbl">Email / Username *</label><input className="inp" value={form.user} onChange={e=>setForm({...form,user:e.target.value})} placeholder="you@gmail.com"/></div>
          <div className="fg"><label className="lbl">Password / App Key *</label><input className="inp" type="password" value={form.pass} onChange={e=>setForm({...form,pass:e.target.value})}/></div>
        </div>
        <div className="fr2">
          <div className="fg"><label className="lbl">From Name</label><input className="inp" value={form.fromName} onChange={e=>setForm({...form,fromName:e.target.value})} placeholder="John from Acme"/></div>
          <div className="fg"><label className="lbl">From Email *</label><input className="inp" value={form.fromEmail} onChange={e=>setForm({...form,fromEmail:e.target.value})} placeholder="john@acme.com"/></div>
        </div>
        <div className="chk"><input type="checkbox" checked={form.isDefault} onChange={e=>setForm({...form,isDefault:e.target.checked})}/><label style={{fontSize:12.5,color:'var(--txt2)'}}>Set as default</label></div>
      </div>
      <div className="modal-ft">
        <button className="btn btn-s" onClick={handleTest} disabled={testing}><Wifi size={15}/> {testing?'Testing...':'Test Connection'}</button>
        <button className="btn btn-p" onClick={handleSave}><CheckCircle size={15}/> Save</button>
      </div>
    </div></div>}

    {showTest && <div className="modal-bg" onClick={()=>setShowTest(null)}><div className="modal" onClick={e=>e.stopPropagation()}>
      <div className="modal-hdr"><h3>Send Test Email</h3><button className="btn btn-g btn-sm" onClick={()=>setShowTest(null)}><X size={15}/></button></div>
      <div className="modal-body">
        <div className="fg"><label className="lbl">Send test email to</label><input className="inp" value={testEmailAddr} onChange={e=>setTestEmailAddr(e.target.value)} placeholder="your-test@email.com"/></div>
      </div>
      <div className="modal-ft"><button className="btn btn-p" onClick={()=>handleSendTest(showTest)}><SendHorizontal size={15}/> Send Test</button></div>
    </div></div>}
  </>);
}
